import React, { FC, useMemo } from 'react';
import { useField } from 'formik';
import { FlexDynamic } from '../../atoms';
import { FieldWrapper, FieldWrapperProps } from './FieldWrapper';
import { RadioInput, RadioInputProps } from './RadioInput';
import { Option, validateRadioCheckOptionsProp } from './utils';

interface UnconnectedRadioGroupProps {
  label: string;
  value: string;
  options: Option[];
  onChange: (value: string) => void;
  disabled?: boolean;
  size?: RadioInputProps['size'];
  name: string;
}

/**
 * A group of radio inputs of which only one can be selected at a time
 * you need to provide the value and onChange handlers to manage the state of the group
 * do not add state to this component
 * @name UnconnectedRadioGroup
 * @description use this if you need to compose complex inter-related form elements that include a radio group
 *
 * @example
 * const [selectedValue, setSelectedValue] = useState<string>(''); // state management in parent component
 * <UnconnectedRadioGroup
 *  name="input"
 *  label={t('common:my.label.key')}
 *  options={[{ value: '1', label: 'option 1' }, { value: '2', label: 'option 2' }]}
 *  value={selectedValue}
 *  onChange={(newValue) => setSelectedValue(newValue)}
 * />
 */
const UnconnectedRadioGroup: FC<UnconnectedRadioGroupProps> = ({
  label,
  onChange,
  options: optionsProp,
  value,
  disabled = false,
  size = 'lg',
  name,
}) => {
  const options = useMemo(
    () => validateRadioCheckOptionsProp({ options: optionsProp, componentName: 'UnconnectedRadioGroup' }),
    [optionsProp]
  );

  return (
    <FlexDynamic aria-label={label} role="radiogroup" direction="column" gap="2" mt="2" w="full">
      {options.map((option) => (
        <RadioInput
          key={option.value}
          value={option.value}
          label={option.label}
          checked={value === option.value}
          onChange={(newValue) => onChange(newValue)}
          disabled={disabled}
          size={size}
          name={name}
        />
      ))}
    </FlexDynamic>
  );
};

export interface WrappedUnconnectedRadioGroupProps {
  inputProps: UnconnectedRadioGroupProps;
  wrapperProps?: FieldWrapperProps;
}

export const WrappedUnconnectedRadioGroup: FC<WrappedUnconnectedRadioGroupProps> = ({ wrapperProps, inputProps }) => {
  return (
    <FieldWrapper {...wrapperProps}>
      <UnconnectedRadioGroup {...inputProps} />
    </FieldWrapper>
  );
};

type ControlledRadioGroupProps = Omit<UnconnectedRadioGroupProps, 'onChange' | 'value'>;

export interface RadioGroupInputProps {
  /** full props for the underlying UnconnectedRadioGroup component */
  inputProps?: Omit<ControlledRadioGroupProps, 'options'>;
  /** full props for the underlying FieldWrapper component */
  wrapperProps?: FieldWrapperProps;
  /** array of configuration for the radio options in the group */
  options: ControlledRadioGroupProps['options'];
  /** name of the form field in the Formik form values */
  name: string;
  /** disable the radio inputs in the group */
  disabled?: boolean;
  /** label for the form field radiogroup */
  label: FieldWrapperProps['label'];
  /** help text for the form field */
  helpText?: FieldWrapperProps['helpText'];
  /** size for the radio elements */
  size?: RadioInputProps['size'];
}

/**
 * A group of radio inputs of which only one can be selected at a time, tied into a Formik Field's state
 * @name RadioGroupInput
 * @description this should be used as the normal component for groups of radio inputs for all Formik forms.
 * it will automatically render any validation errors and will correctly set a11y roles for the elements
 *
 * @example
 * <RadioGroupInput
 *  name="input"
 *  label={t('common:my.label.key')}
 *  options={[{ value: '1', label: 'option 1' }, { value: '2', label: 'option 2' }]}
 * />
 */
export const RadioGroupInput: FC<RadioGroupInputProps> = ({
  disabled,
  helpText,
  inputProps,
  label,
  name,
  options,
  wrapperProps,
  size = 'lg',
}) => {
  const [input, meta, helpers] = useField<string>(name);
  const { value: formikValue } = input;
  const { error } = meta;
  const hasError = !!error;
  const wrapperValidation = hasError ? { validation: { valid: !hasError, message: error?.toString() } } : {};

  const onChange = (newVal: string) => {
    helpers.setValue(newVal);
  };

  // this prevents the console warning about switching between
  // controlled and uncontrolled input types
  let inputValue = formikValue;

  if (inputValue === undefined) {
    inputValue = null;
  }

  return (
    <WrappedUnconnectedRadioGroup
      inputProps={{ ...inputProps, name, label, options, onChange, value: inputValue, disabled, size }}
      wrapperProps={{ ...wrapperProps, ...wrapperValidation, id: name, disabled, label, helpText }}
    />
  );
};
